import { apiFetch } from "./apiFetch";

export type ModerationAction = "FLAGGED" | "DELETED";

export interface ModerationFlag {
  id: string;
  meetingId: string;
  messageId: string;
  senderId: string;
  senderName: string;
  content: string;
  action: ModerationAction;
  categories: string[];
  confidence: number;
  reason: string;
  createdAt: string;
  meeting?: {
    id: string;
    title: string;
    externalMeetingId: string;
  };
}

export interface Paging {
  count: number;
  total?: number;
  limit: number;
  nextToken?: string | null;
}

export interface ModerationConfig {
  enabled: boolean;
  deleteThreshold: number;
  flagThreshold: number;
  categories?: {
    SEXUAL?: {
      deleteThreshold: number;
      flagThreshold: number;
    };
  };
  timeoutMs: number;
  language: string;
}

export interface DeploymentStatus {
  state:
    | "DEPLOYING"
    | "BAKING"
    | "COMPLETE"
    | "ROLLING_BACK"
    | "ROLLED_BACK"
    | "UNKNOWN";
  deploymentNumber?: number;
  configurationVersion?: string;
  percentageComplete?: number;
  startedAt?: string;
  completedAt?: string;
}

export interface ModerationConfigResponse {
  config: ModerationConfig;
  deployment: DeploymentStatus;
}

export interface BlocklistItem {
  type: "emojis" | "emoticons" | "words";
  value: string;
}

export interface Blocklist {
  emojis: string[];
  emoticons: string[];
  words: string[];
}

export interface Allowlist {
  words: string[];
}

export interface ModerationFlagsResponse {
  data: ModerationFlag[];
  paging: Paging;
}

export const ModerationResource = {
  getFlags: (params?: {
    meetingId?: string;
    action?: ModerationAction;
    limit?: number;
    nextToken?: string;
  }) =>
    apiFetch<ModerationFlagsResponse>("/admin/chat/moderation/flags", {
      searchParams: params,
    }),

  getConfig: () =>
    apiFetch<ModerationConfigResponse>("/admin/chat/moderation/config"),

  updateConfig: (config: ModerationConfig) =>
    apiFetch<ModerationConfig>("/admin/chat/moderation/config", {
      method: "PUT",
      body: config,
    }),

  getBlocklist: () =>
    apiFetch<Blocklist>("/admin/chat/moderation/blocklist"),

  addBlocklistItem: (item: BlocklistItem) =>
    apiFetch<{ success: boolean }>("/admin/chat/moderation/blocklist", {
      method: "POST",
      body: item,
    }),

  deleteBlocklistItem: (item: BlocklistItem) =>
    apiFetch<{ success: boolean }>("/admin/chat/moderation/blocklist", {
      method: "DELETE",
      body: item,
    }),

  getAllowlist: () =>
    apiFetch<Allowlist>("/admin/chat/moderation/allowlist"),

  addAllowlistWord: (value: string) =>
    apiFetch<{ success: boolean }>("/admin/chat/moderation/allowlist", {
      method: "POST",
      body: { value },
    }),

  deleteAllowlistWord: (value: string) =>
    apiFetch<{ success: boolean }>("/admin/chat/moderation/allowlist", {
      method: "DELETE",
      body: { value },
    }),
};

// ---------------------------------------------------------------------------
// Moderation review queue
//
// A different endpoint from the chat flags above, and deliberately so. That one
// is shaped around a chat message — it requires meetingId, messageId, senderId
// and confidence — and a refused picture has none of the first three while a
// member's report has no score at all, so those rows are filtered out of it.
//
// The two also read `status` in opposite directions: the chat endpoint projects
// it onto an `action` of FLAGGED/DELETED, reading APPROVED as "approved for
// deletion", while this one uses the schema's meaning — APPROVED means the
// content is acceptable. Keep the vocabularies apart in the UI; a moderator
// should never see one row described two ways.
// ---------------------------------------------------------------------------

export type ModerationContentType =
  | "CHAT_MESSAGE"
  | "PROFILE_PICTURE"
  | "USER";

export type ModerationSource =
  | "COMPREHEND"
  | "REKOGNITION"
  | "MEMBER"
  | "GUEST";

export type ModerationReviewStatus = "PENDING" | "APPROVED" | "REJECTED";

export interface ModerationQueueFlag {
  id: string;
  contentType: ModerationContentType;
  source: ModerationSource;
  /**
   * The artifact under review, in the only form its kind can express: message
   * text for a chat flag, the retained object key for an image, the reported
   * category for a report.
   */
  content: string;
  categories: string[];
  status: ModerationReviewStatus;
  createdAt: string;
  meetingId?: string;
  messageId?: string;
  /** Whose picture or message. Absent on a free-text report. */
  senderId?: string;
  senderName?: string;
  reporterId?: string;
  reportId?: string;
  /**
   * The model's score. **Absent on a member or guest report** — a report is an
   * assertion, not a probability. Render its absence, never a number.
   */
  confidence?: number;
  providerResponse?: string;
  /**
   * Short-lived presigned GET, only on an image the automated gate refused. A
   * reported picture has no object of its own, and the link expires — refetch
   * rather than cache it.
   */
  thumbnailUrl?: string;
  reviewedAt?: string;
  reviewedBy?: string;
  reviewNotes?: string;
  /**
   * When a moderator placed the picture under legal hold. Present only on a
   * held flag; a hold outlives any verdict and only a developer releases it.
   */
  preservedAt?: string;
  preservedBy?: string;
}

export interface ModerationPreserveResult {
  id: string;
  status: "PRESERVED";
  /** True when the flag was already held and nothing was copied again. */
  alreadyHeld: boolean;
  preservedKeys: string[];
}

export interface ModerationQueuePage {
  data: ModerationQueueFlag[];
  paging: { count: number; limit: number; nextToken?: string };
}

export interface UploadSuspension {
  userId: string;
  suspended: boolean;
  reason?: string;
  suspendedBy?: string;
  suspendedAt?: string;
  /** Absent on an indefinite suspension. */
  expiresAt?: string;
}

export const ModerationReviewResource = {
  getQueue: (params?: {
    status?: ModerationReviewStatus;
    contentType?: ModerationContentType;
    source?: ModerationSource;
    limit?: number;
    nextToken?: string;
  }) =>
    apiFetch<ModerationQueuePage>("/admin/moderation/flags", {
      searchParams: params,
    }),

  approve: (flagId: string, reviewNotes?: string) =>
    apiFetch<{ id: string; status: string; publicUrl?: string }>(
      `/admin/moderation/flags/${encodeURIComponent(flagId)}/approve`,
      { method: "POST", body: { reviewNotes } }
    ),

  /**
   * Upholds the flag, and optionally takes the member's published picture down.
   *
   * `removePicture` is only acted on for a picture flag raised by a person — an
   * automated flag's image was never published, and a conduct report is not about
   * a picture. The server ignores it otherwise rather than trusting the caller.
   */
  reject: (flagId: string, reviewNotes?: string, removePicture?: boolean) =>
    apiFetch<{ id: string; status: string }>(
      `/admin/moderation/flags/${encodeURIComponent(flagId)}/reject`,
      { method: "POST", body: { reviewNotes, removePicture } }
    ),

  /**
   * Places the picture behind a flag under legal hold.
   *
   * Copies it somewhere nothing can delete, marks the flag, and tells nobody —
   * not now, and not on a later takedown of a held flag. Changes no verdict and
   * takes nothing down. 409 when there is no picture to hold.
   */
  preserve: (flagId: string) =>
    apiFetch<ModerationPreserveResult>(
      `/admin/moderation/flags/${encodeURIComponent(flagId)}/preserve`,
      { method: "POST" }
    ),

  getSuspension: (userId: string) =>
    apiFetch<UploadSuspension>(
      `/admin/moderation/uploads/${encodeURIComponent(userId)}/suspension`
    ),

  suspendUploads: (
    userId: string,
    body: { reason?: string; expiresAt?: string }
  ) =>
    apiFetch<UploadSuspension>(
      `/admin/moderation/uploads/${encodeURIComponent(userId)}/suspension`,
      { method: "PUT", body }
    ),

  liftSuspension: (userId: string) =>
    apiFetch<UploadSuspension>(
      `/admin/moderation/uploads/${encodeURIComponent(userId)}/suspension`,
      { method: "DELETE" }
    ),
};
